import React, { useEffect, useRef, useState } from "react";
import styles from "./feed.module.scss";
import FeedCreatePost from "./feedCreatePost";
import FeedCard from "./feedCard";
import { FeedAPI } from "./feed.service";
import { useSelector } from "react-redux";
import { KoAffix, KoBadge, KoLoader, KoText } from "packages";
import FeedSkeletonList from "./feedSkeletonList";
import Trending from "./trending";
import KoIcon from "@kwapio/icons";

export default function Feed() {
  const { data: user } = useSelector((s) => s.userById);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [counts, setCounts] = useState({});
  const bottomRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [activeFeedType, setActiveFeedType] = useState('all');

  const [pagination, setPagination] = useState({
    page: 1,
    size: 5,
    offset: 0
  });

  const loadFeeds = async({ reset = false } = {}) => {
    if (isFetchingRef.current || loading) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const params = reset
        ? { page: 1, size: pagination.size, offset: 0 }
        : pagination;

      const apiFn = getFeedApiByType();
      const res = await apiFn(params);

      const next =
        res?.feeds?.map((p) => ({
          ...p,
          comments: p.comments || [],
          image: p.attachments?.[0]?.url || null,
          author: p.createdByProfile
        })) || [];

      setPosts((prev) => {
        if (reset) return next;
        const ids = new Set(prev.map((p) => p.feedId));
        return [...prev, ...next.filter((p) => !ids.has(p.feedId))];
      });

      if (next.length < pagination.size) setHasMore(false);
      if (reset) setHasMore(true);
    }catch(error){
      setLoading(false);
      console.error("eError",error);
      
    }
    finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    loadFeeds({ reset: true });
  }, [activeFeedType]);


  useEffect(() => {
    FeedAPI.getCounts().then((res) => {
      setCounts(res);
    });
  }, []);

  useEffect(() => {
    if (!bottomRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !loading) {
          setPagination((p) => ({
            ...p,
            page: p.page + 1,
            offset: p.offset + p.size
          }));
        }
      },
      { threshold: 1 }
    );

    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore]);

  useEffect(() => {
    if (pagination.page > 1) loadFeeds();
  }, [pagination.page]);

  const refreshFeeds = () => {
    setPagination({ page: 1, size: 5, offset: 0 });
    setHasMore(true);
    loadFeeds({ reset: true });
  };

  const getFeedApiByType = () => {
    switch (activeFeedType) {
    case "my":
      return FeedAPI.getMyFeeds;
    case "scheduled":
      return FeedAPI.getShudledFeeds;
    case "saved":
      return FeedAPI.getSavedFeeds;
    default:
      return FeedAPI.getFeeds;
    }
  };

  const onEditPost = (post) => {
    setPosts((prev) => prev.map((p) => (p.feedId === post.feedId ? post : p)));
  };

  const onDeletePost = (feedId) => {
    setPosts((prev) => prev.filter((p) => p.feedId !== feedId));
  };

  const onLikePost = (post) => {
    setPosts((prev) => prev.map((p) => (p.feedId === post.feedId ? post : p)));
  };

  const TABS = [
    {
      key: 'all',
      label: (
        <div className={styles.trendingTab}>
          <KoIcon
            name="FileOutlined"
            width={16}
            height={16}
            style={{ marginRight: "0.5rem" }}
          />
          <KoText text="All Feeds" />
        </div>
      ),
      children: ""
    },
    // {
    //   key: 2,
    //   label: (
    //     <div className={styles.trendingTab}>
    //       <KoIcon
    //         name="DeploymentOutlined"
    //         width={16}
    //         height={16}
    //         style={{ marginRight: "0.5rem" }}
    //       />
    //       <KoText text="Trending" />
    //     </div>
    //   ),
    //   children: ""
    // },
    {
      key: 'my',
      label: (
        <div className={styles.trendingTab}>
          <KoIcon
            name="FileOutlined"
            width={16}
            height={16}
            style={{ marginRight: "0.5rem" }}
          />
          <KoText text="My Feeds" />
          <KoBadge
            count={counts?.published}
            className={styles.feedCountBadge}
          />
        </div>
      ),
      children: ""
    },
    {
      key: 'scheduled',
      label: (
        <div className={styles.trendingTab}>
          <KoIcon
            name="LogOutlined"
            width={16}
            height={16}
            style={{ marginRight: "0.5rem" }}
          />
          <KoText text="Scheduled Feeds" />
          <KoBadge count={counts?.queued} className={styles.feedCountBadge} />
        </div>
      ),
      children: ""
    },
    // {
    //   key: 'polls',
    //   label: (
    //     <div className={styles.trendingTab}>
    //       <KoIcon
    //         name="PollOutlined"
    //         width={16}
    //         height={16}
    //         style={{ marginRight: "0.5rem" }}
    //       />
    //       <KoText text="Polls" />
    //     </div>
    //   ),
    //   children: ""
    // },
    {
      key: 'saved',
      label: (
        <div className={styles.trendingTab}>
          <KoIcon
            name="BookMarkOutlined"
            width={16}
            height={16}
            style={{ marginRight: "0.5rem" }}
          />
          <KoText text="Bookmarks" />
        </div>
      ),
      children: ""
    }
  ];

  // const CHANNELS = [
  //   {
  //     key: "general",
  //     label: (
  //       <div className={styles.trendingTab}>
  //         <KoIcon
  //           name="WebOutlined"
  //           width={16}
  //           height={16}
  //           style={{ marginRight: "0.5rem" }}
  //         />
  //         <KoText text="Linked in" />
  //       </div>
  //     ),
  //     children: ""
  //   },
  //   {
  //     key: "development",
  //     label: (
  //       <div className={styles.trendingTab}>
  //         <KoIcon
  //           name="WebOutlined"
  //           width={16}
  //           height={16}
  //           style={{ marginRight: "0.5rem" }}
  //         />
  //         <KoText text="Facebook" />
  //       </div>
  //     ),
  //     children: ""
  //   },
  //   {
  //     key: "design",
  //     label: (
  //       <div className={styles.trendingTab}>
  //         <KoIcon
  //           name="WebOutlined"
  //           width={16}
  //           height={16}
  //           style={{ marginRight: "0.5rem" }}
  //         />
  //         <KoText text="Whatsapp" />
  //       </div>
  //     ),
  //     children: ""
  //   },
  //   {
  //     key: "design",
  //     label: (
  //       <div className={styles.trendingTab}>
  //         <KoIcon
  //           name="WebOutlined"
  //           width={16}
  //           height={16}
  //           style={{ marginRight: "0.5rem" }}
  //         />
  //         <KoText text="Instagram" />
  //       </div>
  //     ),
  //     children: ""
  //   }
  // ];

  const getFilteredCards = (key) => {
    let type = "all";
    switch (key) {
    case 'all':
      type = 'all';
      break;
    case 'my':
      type = 'my';
      break;
    case 'scheduled':
      type = 'scheduled';
      break;
    case 'saved':
      type = 'saved';
      break;
    default:
      type = 'all';
      break;
    }

    setActiveFeedType(type);
    setPagination({ page: 1, size: 5, offset: 0 });
    setHasMore(true);
    setPosts([]);

    loadFeeds({ reset: true });
  };


  return (
    <div className={styles.feedWrapper}>
      <KoAffix top={50} className={styles.createPostAffix}>
        <div className={styles.spacerProfile}>
          <div className={styles.profile}>
            {/* <div className={styles.profileHeader}>
              {user?.profile?.logo?.[0]?.url ? (
                <img
                  src={
                    user?.profile?.logo?.[0]?.url ??
                    user?.profile?.logo?.[0]?.url
                  }
                  className={styles.avatar}
                  alt="avatar"
                />
              ) : (
                <div className={styles.avatar}>
                  {user?.profile?.firstName?.charAt(0)?.toUpperCase() || ""}
                  {user?.profile?.lastName?.charAt(0)?.toUpperCase() || ""}
                </div>
              )}
              <div className={styles.profileInfo}>
                <KoTitle
                  level={4}
                  text={`${user?.profile?.firstName || ""} ${user?.profile?.lastName || ""}`}
                  className={styles.profileName}
                />
                <KoText
                  text={user?.email || ""}
                  className={styles.profileDesignation}
                />
              </div>
            </div> */}
            <ul className={styles.profileList}>
              {TABS.map((tab) => (
                <li
                  key={tab.key}
                  onClick={() => getFilteredCards(tab.key)}
                  className={`${styles.profileItem} ${activeFeedType === tab.key && styles.active}`}
                >
                  {tab.label}
                </li>
              ))}
            </ul>
            {/* <KoTitle
              level={5}
              text="Popular Channels"
              className={styles.channelsTitle}
            />
            <ul className={styles.channelsList}>
              {CHANNELS.map((tab) => (
                <li key={tab.key} className={styles.profileItem}>
                  {tab.label}
                </li>
              ))}
            </ul> */}
          </div>
        </div>
      </KoAffix>
      <div className={styles.feed}>
        <FeedCreatePost user={user} getFeeds={refreshFeeds} />

        {loading && posts.length === 0 && <FeedSkeletonList count={4} />}

        {posts.map((post) => (
          <FeedCard
            key={post.feedId}
            post={post}
            user={user}
            onEditPost={onEditPost}
            onDeletePost={onDeletePost}
            onLikePost={onLikePost}
          />
        ))}

        <div ref={bottomRef} className={styles.lazyLoaderTrigger} />

        {loading && posts.length > 0 && (
          <div className={styles.loadingMore}>
            <KoLoader circle={false} />
          </div>
        )}

        {!hasMore && posts.length > 0 && (
          <div className={styles.endText}>{`You've reached the end`}</div>
        )}
      </div>
      <KoAffix top={50} className={styles.createPostAffix}>
        <div className={styles.trandingContnet}>
          <Trending />
        </div>
      </KoAffix>
    </div>
  );
}
